// Iframe session bootstrap.
//
// The Rumio admin loads us with `?rumio_session=<jwt>` on the iframe URL.
// We grab it once at boot, stash it in sessionStorage so subsequent
// page-loads inside the iframe can re-use it, and expose a getter for the
// fetch wrapper to attach as `Authorization: Bearer`.
//
// Token refresh lives in session-refresh.ts: host tokens expire after
// 300 s and are renewed over postMessage, landing here via setSessionToken.

const STORAGE_KEY = 'rumio_session_jwt';

let inMemoryToken: string | null = null;

export function captureSessionFromUrl(url: string = window.location.href): string | null {
  try {
    const parsed = new URL(url);
    const fromQuery = parsed.searchParams.get('rumio_session');
    if (fromQuery) {
      setSessionToken(fromQuery);
      // Strip the token from the URL so it doesn't end up in browser
      // history or referrer headers.
      parsed.searchParams.delete('rumio_session');
      const cleaned = parsed.pathname + (parsed.search ? parsed.search : '') + parsed.hash;
      try {
        window.history.replaceState(null, '', cleaned);
      } catch {
        // ignore — non-browser test envs may not implement replaceState
      }
      return fromQuery;
    }
  } catch {
    // Malformed URL — fall through to storage lookup.
  }

  if (!inMemoryToken) {
    try {
      inMemoryToken = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      inMemoryToken = null;
    }
  }
  return inMemoryToken;
}

export function setSessionToken(token: string): void {
  inMemoryToken = token;
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // sessionStorage unavailable (e.g. third-party cookie blocked, very
    // private browsing); the in-memory copy still works for this load.
  }
}

export function getSessionToken(): string | null {
  if (inMemoryToken) return inMemoryToken;
  try {
    inMemoryToken = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    inMemoryToken = null;
  }
  return inMemoryToken;
}

export function clearSession(): void {
  inMemoryToken = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// For tests — wipes in-memory state without touching storage.
export function _resetForTests(): void {
  inMemoryToken = null;
}
